/**
 * Applies a story beat: navigates to its mode, writes its deep-link state
 * through the stores, runs its actions in order (polling for the globe
 * engine, the twin handle or the loaded asset where an action needs them)
 * and then waits until its readiness checks hold or the timeout passes.
 *
 * Everything outside this module comes in through ControllerDeps, so the
 * tests drive it with fakes. freeze() and unfreeze() live here for the same
 * reason; the presenter session calls them with the real stores.
 */
import { MODES, type Mode, type Selection } from '@/state/store';
import type { SceneMode, OrbitPathMode } from '@/features/loader/globe/GlobeEngine';
import type { NetworkId } from '@/features/loader/types';
import type { LoaderProfile } from '@/features/loader/store';
import type { LiveSource } from '@/features/twin/store';
import type { Procedure, Subsystem, ViewMode } from '@/features/twin/types';
import type { Beat, BeatAction, BeatState, ReadyCheck } from './beats';

// ------------------------------------------------------------------ ports

/** The parts of useUI the controller uses. */
export interface UiPort {
  presenter: boolean;
  selection: Selection;
  gaiaOpen: boolean;
  rightPanelOpen: boolean;
  select: (partial: Partial<Selection>) => void;
  setBottomTab: (tab: string) => void;
  setGaiaOpen: (open: boolean) => void;
  toggleRightPanel: () => void;
  togglePresenter: () => void;
}

/** The parts of useClock the controller uses. */
export interface ClockPort {
  live: boolean;
  playing: boolean;
  multiplier: number;
  simTime: number;
  setLive: () => void;
  setSimTime: (ms: number) => void;
  setPlaying: (playing: boolean) => void;
  setMultiplier: (multiplier: number) => void;
}

export interface DatasetPort {
  network: NetworkId;
  source: 'api' | 'static' | 'live';
  antennas: Array<{ id: string; location: { lat: number; lon: number } }>;
  missions: Array<{ id: string }>;
}

/** The parts of useLoader the controller uses. */
export interface LoaderPort {
  network: NetworkId;
  datasetStatus: 'idle' | 'loading' | 'ready' | 'error';
  dataset: DatasetPort | null;
  results: { antennaUtil: Record<string, unknown> } | null;
  trackedMissionId: string | null;
  guamDemoActive: boolean;
  profile: LoaderProfile;
  /** What-if set (missions in the analysis and drawn on the globe). */
  activeMissionIds: string[];
  /** Globe filter (null shows every active mission). */
  visibleMissionIds: string[] | null;
  setActiveMissionIds: (ids: string[]) => void;
  setVisibleMissionIds: (ids: string[] | null) => void;
  setNetwork: (network: NetworkId) => void;
  setProfile: (profile: LoaderProfile) => void;
  setView: (
    patch: Partial<{
      sceneMode: SceneMode;
      orbitPathMode: OrbitPathMode;
      coverageEnabled: boolean;
      contactLinesEnabled: boolean;
      nightLights: boolean;
    }>,
  ) => void;
  setTracked: (missionId: string | null) => void;
  requestAnalysis: () => void;
}

/** The parts of useTwin the controller uses. */
export interface TwinPort {
  assetId: string | null;
  loadStatus: 'idle' | 'loading' | 'ready' | 'error';
  loadError: string | null;
  element: { id: string; subsystems: Subsystem[] } | null;
  procedure: { procedure: { id: string }; playing: boolean } | null;
  liveSource: LiveSource;
  failureScenarios: string[];
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  setExplode: (value: number) => void;
  setSignalPath: (id: string | null) => void;
  setSubview: (id: string | null) => void;
  startProcedure: (procedure: Procedure) => void;
  stopProcedure: () => void;
  setProcedurePlaying: (playing: boolean) => void;
  setLiveSource: (source: LiveSource) => void;
  setFailureScenarios: (ids: string[]) => void;
}

export interface LiveFeedPort {
  siteId: string | null;
  lastPayloadAt: number | null;
}

export interface OverviewPort {
  setHighlightedStage: (stageId: number | null) => void;
}

/** The two GlobeEngine calls the controller makes. */
export interface GlobePort {
  flyTo(lon: number, lat: number, heightM?: number, durationS?: number): void;
  getTracked(): string | null;
}

/** The TwinHandle call the controller makes: resolves when the flight ends. */
export interface TwinHandlePort {
  flyTo(target: { poseId?: string; subsystemId?: string }, durationMs?: number): Promise<void>;
}

export interface GuamPort {
  /** Stop the demo and remove the Guam station from the dataset if it was added. */
  reset(): void;
  start(): void;
  addStation(): void;
  stationAdded(): boolean;
}

export interface ControllerDeps {
  navigate(path: string): void;
  pathname(): string;
  /** location.search ('' or '?a=b'). */
  search(): string;
  /** The search the router has rendered; lags search() while BrowserRouter renders a navigation in a transition. */
  renderedSearch(): string;
  ui(): UiPort;
  clock(): ClockPort;
  loader(): LoaderPort;
  twin(): TwinPort;
  liveFeed(): LiveFeedPort;
  overview(): OverviewPort;
  globe(): GlobePort | null;
  twinHandle(): TwinHandlePort | null;
  guam: GuamPort;
  loadProcedure(id: string, ctx: { assetId: string; subsystems: Subsystem[] }): Promise<Procedure>;
  gaiaPrefill(prompt: string): void;
  /** Resolves after ms, or early when the signal aborts (never rejects). */
  sleep(ms: number, signal?: AbortSignal): Promise<void>;
  now(): number;
  reducedMotion(): boolean;
}

// ---------------------------------------------------------------- results

export type BeatOutcome =
  | { status: 'ready'; beatId: string; skipped: string[]; elapsedMs: number }
  | { status: 'not-ready'; beatId: string; missing: string[]; skipped: string[]; elapsedMs: number }
  | { status: 'cancelled'; beatId: string };

export interface ApplyOptions {
  signal?: AbortSignal;
  /** How long the readiness checks may take once the actions have run. */
  readyTimeoutMs?: number;
  /** How long an action waits for the globe engine, the twin handle or the loaded asset. */
  engineTimeoutMs?: number;
  pollMs?: number;
}

export const READY_TIMEOUT_MS = 10_000;
export const ENGINE_TIMEOUT_MS = 5_000;
export const POLL_MS = 100;
/** Globe flight to an antenna: low enough to read as a dive, inside the 800 to 1500 ms motion rule. */
export const ANTENNA_FLIGHT_HEIGHT_M = 1_200_000;
export const ANTENNA_FLIGHT_S = 1.5;
export const TWIN_FLIGHT_MS = 1200;
/** Extra time a twin flight may take to report completion before the beat moves on. */
const FLIGHT_GRACE_MS = 1500;

const CANCELLED = Symbol('cancelled');
type StepResult = string | null | typeof CANCELLED;

// ---------------------------------------------------------------- routes

/** True when the pathname shows this mode (/twin/<asset> is Maintenance). */
export function isOnMode(pathname: string, mode: Mode): boolean {
  if (pathname === `/${mode}` || pathname.startsWith(`/${mode}/`)) return true;
  return mode === 'maintenance' && pathname.startsWith('/twin/');
}

/** The mode a pathname shows, or null off the mode routes. */
export function modeFromPath(pathname: string): Mode | null {
  if (pathname.startsWith('/twin/')) return 'maintenance';
  const first = pathname.split('/')[1] ?? '';
  return (MODES as readonly string[]).includes(first) ? (first as Mode) : null;
}

function goToMode(mode: Mode, deps: ControllerDeps): void {
  if (!isOnMode(deps.pathname(), mode)) deps.navigate(`/${mode}`);
}

// ----------------------------------------------------------------- state

function applyClock(time: string, speed: number | undefined, deps: ControllerDeps): void {
  const clock = deps.clock();
  if (time === 'live') {
    if (speed !== undefined) throw new Error(`speed ${speed} needs a simulated time, not 'live'`);
    clock.setLive();
    return;
  }
  const ms = time === 'now' ? deps.now() : Date.parse(time);
  if (!Number.isFinite(ms)) throw new Error(`time "${time}" is not 'live', 'now' or an ISO date`);
  clock.setSimTime(ms);
  clock.setMultiplier(speed ?? 1);
  clock.setPlaying(true);
}

/** Writes the deep-link part of a beat: network, clock, selection, bottom tab, detail panel. */
export function applyBeatState(state: BeatState, deps: ControllerDeps): void {
  if (state.network && deps.loader().network !== state.network) deps.loader().setNetwork(state.network);
  if (state.time !== undefined) applyClock(state.time, state.speed, deps);
  else if (state.speed !== undefined) throw new Error(`speed ${state.speed} without a time`);
  if (state.selection) deps.ui().select(state.selection);
  if (state.tab) deps.ui().setBottomTab(state.tab);
  if (state.panel) {
    const open = deps.ui().rightPanelOpen;
    if ((state.panel === 'open') !== open) deps.ui().toggleRightPanel();
  }
}

// ------------------------------------------------------------- waiting

interface WaitContext {
  deps: ControllerDeps;
  signal?: AbortSignal;
  engineTimeoutMs: number;
  pollMs: number;
}

async function waitFor(
  test: () => boolean,
  ctx: WaitContext,
  timeoutMs: number,
): Promise<'ok' | 'timeout' | typeof CANCELLED> {
  const deadline = ctx.deps.now() + timeoutMs;
  for (;;) {
    if (ctx.signal?.aborted) return CANCELLED;
    if (test()) return 'ok';
    if (ctx.deps.now() >= deadline) return 'timeout';
    await ctx.deps.sleep(ctx.pollMs, ctx.signal);
  }
}

function twinSettled(deps: ControllerDeps): boolean {
  const target = deps.ui().selection.assetId;
  const twin = deps.twin();
  return twin.assetId === target && (twin.loadStatus === 'ready' || twin.loadStatus === 'error');
}

/** Waits until the selected asset is loaded in the twin store; returns a note when it is not. */
/**
 * Waits until the selected asset is loaded in the twin store and, with needScene, its scene is
 * mounted (the handle is published); returns a note when it is not.
 */
async function waitForTwin(ctx: WaitContext, what: string, needScene: boolean): Promise<StepResult> {
  const r = await waitFor(
    () => twinSettled(ctx.deps) && (!needScene || ctx.deps.twinHandle() !== null),
    ctx,
    ctx.engineTimeoutMs,
  );
  if (r === CANCELLED) return CANCELLED;
  const target = ctx.deps.ui().selection.assetId;
  const twin = ctx.deps.twin();
  if (twin.assetId === target && twin.loadStatus === 'error') {
    return `${what}: twin ${target} failed to load (${twin.loadError ?? 'no message'})`;
  }
  if (r === 'timeout') {
    const why = !twinSettled(ctx.deps)
      ? `twin ${target ?? '(no asset selected)'} not loaded`
      : 'twin scene not mounted';
    return `${what}: ${why} after ${ctx.engineTimeoutMs / 1000} s`;
  }
  return null;
}

async function waitForGlobe(ctx: WaitContext, what: string, needDataset: boolean): Promise<StepResult> {
  const r = await waitFor(
    () => ctx.deps.globe() !== null && (!needDataset || ctx.deps.loader().datasetStatus === 'ready'),
    ctx,
    ctx.engineTimeoutMs,
  );
  if (r === CANCELLED) return CANCELLED;
  if (r === 'timeout') {
    const why = ctx.deps.globe() === null ? 'globe engine not mounted' : 'network data not loaded';
    return `${what}: ${why} after ${ctx.engineTimeoutMs / 1000} s`;
  }
  return null;
}

async function waitForDataset(ctx: WaitContext, what: string): Promise<StepResult> {
  const r = await waitFor(() => ctx.deps.loader().datasetStatus === 'ready', ctx, ctx.engineTimeoutMs);
  if (r === CANCELLED) return CANCELLED;
  if (r === 'timeout') return `${what}: network data not loaded after ${ctx.engineTimeoutMs / 1000} s`;
  return null;
}

/** Resolves with 'done', or 'late' when the promise has not settled within ms. Rejections propagate. */
async function withinMs(
  promise: Promise<void>,
  ms: number,
  deps: ControllerDeps,
  signal?: AbortSignal,
): Promise<'done' | 'late'> {
  let settled = false;
  const tracked = promise.then(() => {
    settled = true;
  });
  await Promise.race([tracked, deps.sleep(ms, signal)]);
  if (settled) return 'done';
  // The flight keeps going; a rejection after this point is still reported.
  tracked.catch((err: unknown) =>
    console.error('[presenter] twin flight failed after the beat moved on:', err),
  );
  return 'late';
}

// --------------------------------------------------------------- actions

export function describeAction(action: BeatAction): string {
  switch (action.kind) {
    case 'mode':
      return `mode ${action.mode}`;
    case 'wait':
      return `wait ${action.ms} ms`;
    case 'sceneMode':
      return `scene ${action.mode}`;
    case 'orbitPaths':
      return `orbit paths ${action.mode}`;
    case 'coverage':
      return `coverage ${action.enabled ? 'on' : 'off'}`;
    case 'contactLines':
      return `contact lines ${action.enabled ? 'on' : 'off'}`;
    case 'nightLights':
      return `night lights ${action.enabled ? 'on' : 'off'}`;
    case 'follow':
      return action.missionId ? `follow ${action.missionId}` : 'stop following';
    case 'loaderProfile':
      return `loader profile ${action.profile}`;
    case 'flyToAntenna':
      return `fly to ${action.antennaId}`;
    case 'runAnalysis':
      return 'run analysis';
    case 'guam':
      return `Guam demo ${action.step}`;
    case 'twinView':
      return `twin view ${action.mode}`;
    case 'signalPath':
      return action.id ? `signal path ${action.id}` : 'clear signal path';
    case 'subview':
      return action.id ? `subview ${action.id}` : 'main view';
    case 'twinCamera':
      return `twin camera ${action.subsystemId ?? action.poseId ?? 'main'}`;
    case 'liveSource':
      return `live source ${action.source}`;
    case 'procedure':
      return action.id ? `start procedure ${action.id}` : 'stop procedure';
    case 'failureScenarios':
      return action.ids.length ? `failure scenarios ${action.ids.join(', ')}` : 'clear failure scenarios';
    case 'gaiaPrompt':
      return 'GAIA prompt';
    case 'overviewStage':
      return action.stageId === null ? 'clear overview stage' : `overview stage ${action.stageId}`;
  }
}

/** Runs one action. Returns null when done, a note when it had to be skipped, or CANCELLED. Throws on real failures. */
async function runAction(action: BeatAction, ctx: WaitContext): Promise<StepResult> {
  const { deps, signal } = ctx;
  const what = describeAction(action);
  switch (action.kind) {
    case 'mode':
      goToMode(action.mode, deps);
      return null;
    case 'wait':
      await deps.sleep(action.ms, signal);
      return signal?.aborted ? CANCELLED : null;
    case 'sceneMode':
      deps.loader().setView({ sceneMode: action.mode });
      return null;
    case 'orbitPaths':
      deps.loader().setView({ orbitPathMode: action.mode });
      return null;
    case 'coverage':
      deps.loader().setView({ coverageEnabled: action.enabled });
      return null;
    case 'contactLines':
      deps.loader().setView({ contactLinesEnabled: action.enabled });
      return null;
    case 'nightLights':
      if (action.enabled && typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'night lights skipped: offline (the layer streams from Cesium ion)';
      }
      deps.loader().setView({ nightLights: action.enabled });
      return null;
    case 'follow': {
      if (action.missionId === null) {
        deps.loader().setTracked(null);
        return null;
      }
      // LoaderGlobe tracks only when trackedMissionId changes, and only a mission entity that is drawn,
      // so the follow waits for the network's data and re-arms a follow the engine has lost.
      const note = await waitForGlobe(ctx, what, true);
      // A cancelled beat must not touch the follow: the beat that replaced it owns it now.
      if (note === CANCELLED) return CANCELLED;
      if (note) {
        deps.loader().setTracked(action.missionId);
        return note;
      }
      const missionId = action.missionId;
      if (!deps.loader().dataset?.missions.some((m) => m.id === missionId)) {
        return `${what}: no mission ${missionId} in the ${deps.loader().network} network`;
      }
      // The beat's subject is shown even if the What-if set or the strip filter had it hidden.
      const active = deps.loader().activeMissionIds;
      if (!active.includes(missionId)) {
        deps.loader().setActiveMissionIds([...active, missionId]);
        // The What-if set changed: the schedule on screen must include the mission being presented.
        deps.loader().requestAnalysis();
      }
      const visible = deps.loader().visibleMissionIds;
      if (visible && !visible.includes(missionId))
        deps.loader().setVisibleMissionIds([...visible, missionId]);
      const lost =
        deps.loader().trackedMissionId === action.missionId &&
        deps.globe()?.getTracked() !== action.missionId;
      if (lost) {
        deps.loader().setTracked(null);
        await deps.sleep(ctx.pollMs, signal);
        if (signal?.aborted) return CANCELLED;
      }
      deps.loader().setTracked(action.missionId);
      return null;
    }
    case 'loaderProfile': {
      // LoaderStage puts a ?profile= query back into the store whenever the profile changes, so a
      // conflicting query is dropped first (the other query parameters stay). BrowserRouter renders
      // that navigation in a transition, so the profile is set only once the router has rendered it:
      // before that, LoaderStage would still see the old query and put the old profile back.
      const conflicting = (search: string) => {
        const fromUrl = new URLSearchParams(search).get('profile');
        return fromUrl !== null && fromUrl !== action.profile;
      };
      if (conflicting(deps.search())) {
        const params = new URLSearchParams(deps.search());
        params.delete('profile');
        const rest = params.toString();
        deps.navigate(`${deps.pathname()}${rest ? `?${rest}` : ''}`);
      }
      const rendered = await waitFor(() => !conflicting(deps.renderedSearch()), ctx, ctx.engineTimeoutMs);
      if (rendered === CANCELLED) return CANCELLED;
      if (rendered === 'timeout') return `${what}: the router still shows ${deps.renderedSearch()}`;
      if (deps.loader().profile === action.profile) return null;
      deps.loader().setProfile(action.profile);
      // LoaderStage's effect runs after the render: look again a frame later.
      await deps.sleep(ctx.pollMs, signal);
      if (signal?.aborted) return CANCELLED;
      return deps.loader().profile === action.profile
        ? null
        : `${what}: the profile went back to ${deps.loader().profile}`;
    }
    case 'flyToAntenna': {
      const note = await waitForGlobe(ctx, what, true);
      if (note) return note;
      const antenna = deps.loader().dataset?.antennas.find((a) => a.id === action.antennaId);
      if (!antenna) return `${what}: no antenna ${action.antennaId} in the ${deps.loader().network} network`;
      // A camera that follows a satellite ignores the flight: wait until the engine has let go
      // (the store's setTracked(null) reaches it one React effect later), then give Cesium a frame.
      const wasTracking = deps.globe()?.getTracked() !== null;
      const released = await waitFor(() => deps.globe()?.getTracked() === null, ctx, ctx.engineTimeoutMs);
      if (released === CANCELLED) return CANCELLED;
      if (released === 'timeout')
        return `${what}: the globe still follows ${deps.globe()?.getTracked() ?? 'a mission'}`;
      if (wasTracking) await deps.sleep(ctx.pollMs, signal);
      if (signal?.aborted) return CANCELLED;
      const globe = deps.globe();
      if (!globe) return `${what}: globe engine unmounted`;
      const reduced = deps.reducedMotion();
      globe.flyTo(
        antenna.location.lon,
        antenna.location.lat,
        ANTENNA_FLIGHT_HEIGHT_M,
        reduced ? 0 : ANTENNA_FLIGHT_S,
      );
      if (!reduced) await deps.sleep(ANTENNA_FLIGHT_S * 1000, signal);
      return signal?.aborted ? CANCELLED : null;
    }
    case 'runAnalysis': {
      // The runtime drops a request made before the dataset is in (it has nothing to analyse).
      const note = await waitForDataset(ctx, what);
      if (note) return note;
      deps.loader().requestAnalysis();
      return null;
    }
    case 'guam': {
      if (action.step === 'reset') {
        deps.guam.reset();
        return null;
      }
      const note =
        action.step === 'start' ? await waitForGlobe(ctx, what, true) : await waitForDataset(ctx, what);
      if (note) return note;
      if (action.step === 'start') deps.guam.start();
      else deps.guam.addStation();
      return null;
    }
    case 'twinView': {
      // The twin resets explode and signal path when an asset loads, and the scene (a lazy chunk) can
      // mount later still: view changes wait until the twin is on screen, so their holds are seen.
      const note = await waitForTwin(ctx, what, true);
      if (note) return note;
      const twin = deps.twin();
      twin.setViewMode(action.mode);
      if (action.mode === 'exploded' && action.explode !== undefined) twin.setExplode(action.explode);
      return null;
    }
    case 'signalPath': {
      const note = await waitForTwin(ctx, what, true);
      if (note) return note;
      deps.twin().setSignalPath(action.id);
      return null;
    }
    case 'subview': {
      const note = await waitForTwin(ctx, what, true);
      if (note) return note;
      deps.twin().setSubview(action.id);
      return null;
    }
    case 'twinCamera': {
      const note = await waitForTwin(ctx, what, true);
      if (note) return note;
      const handle = deps.twinHandle();
      if (!handle) return `${what}: twin scene unmounted`;
      const durationMs = deps.reducedMotion() ? 0 : TWIN_FLIGHT_MS;
      const target = action.subsystemId
        ? { subsystemId: action.subsystemId }
        : { poseId: action.poseId ?? 'main' };
      const result = await withinMs(
        handle.flyTo(target, durationMs),
        durationMs + FLIGHT_GRACE_MS,
        deps,
        signal,
      );
      if (signal?.aborted) return CANCELLED;
      return result === 'late' ? `${what}: the flight did not report completion` : null;
    }
    case 'liveSource':
      deps.twin().setLiveSource(action.source);
      return null;
    case 'procedure': {
      if (action.id === null) {
        if (deps.twin().procedure) deps.twin().stopProcedure();
        return null;
      }
      // The first steps move the camera: start once the scene is there to show them.
      const note = await waitForTwin(ctx, what, true);
      if (note) return note;
      const element = deps.twin().element;
      if (!element) return `${what}: the twin has no element loaded`;
      const procedure = await deps.loadProcedure(action.id, {
        assetId: element.id,
        subsystems: element.subsystems,
      });
      if (signal?.aborted) return CANCELLED;
      // Presenter is explicitly automatic; the operator's Procedures panel
      // starts paused. Fit seven maintenance beats plus flights into this beat.
      deps.twin().startProcedure({ ...procedure, steps: procedure.steps.map(step => ({ ...step, dwellMs: 4500 })) });
      deps.twin().setProcedurePlaying(true);
      return null;
    }
    case 'failureScenarios':
      deps.twin().setFailureScenarios([...action.ids]);
      return null;
    case 'gaiaPrompt':
      deps.ui().setGaiaOpen(true);
      deps.gaiaPrefill(action.prompt);
      return null;
    case 'overviewStage':
      deps.overview().setHighlightedStage(action.stageId);
      return null;
  }
}

// ------------------------------------------------------------- readiness

export function describeCheck(check: ReadyCheck): string {
  switch (check.kind) {
    case 'route':
      return `${check.mode} route`;
    case 'globe':
      return 'globe engine';
    case 'dataset':
      return `${check.network} network data`;
    case 'analysis':
      return check.antennaId ? `analysis results with ${check.antennaId}` : 'analysis results';
    case 'tracking':
      return `following ${check.missionId}`;
    case 'guamDemo':
      return 'Guam demo running';
    case 'guamStation':
      return 'Guam station in the network';
    case 'twin':
      return `twin ${check.assetId}`;
    case 'liveFeed':
      return `live feed for ${check.assetId}`;
    case 'procedure':
      return `procedure ${check.id}`;
    case 'gaiaOpen':
      return 'GAIA column open';
  }
}

export function isCheckMet(check: ReadyCheck, deps: ControllerDeps): boolean {
  switch (check.kind) {
    case 'route':
      return isOnMode(deps.pathname(), check.mode);
    case 'globe':
      return deps.globe() !== null;
    case 'dataset': {
      const loader = deps.loader();
      return loader.datasetStatus === 'ready' && loader.dataset?.network === check.network;
    }
    case 'analysis': {
      // Results on screen count, also while a re-run is in progress: the panels keep showing them.
      const results = deps.loader().results;
      if (!results) return false;
      return !check.antennaId || check.antennaId in results.antennaUtil;
    }
    case 'tracking':
      return (
        deps.loader().trackedMissionId === check.missionId && deps.globe()?.getTracked() === check.missionId
      );
    case 'guamDemo':
      return deps.loader().guamDemoActive;
    case 'guamStation':
      return deps.guam.stationAdded();
    case 'twin': {
      const twin = deps.twin();
      return twin.assetId === check.assetId && twin.loadStatus === 'ready' && deps.twinHandle() !== null;
    }
    case 'liveFeed': {
      const feed = deps.liveFeed();
      return feed.siteId === check.assetId && feed.lastPayloadAt !== null;
    }
    case 'procedure':
      return deps.twin().procedure?.procedure.id === check.id;
    case 'gaiaOpen':
      return deps.ui().gaiaOpen;
  }
}

/** Descriptions of the checks that do not hold yet (empty when the beat counts as shown). */
export function missingChecks(checks: readonly ReadyCheck[], deps: ControllerDeps): string[] {
  return checks.filter((c) => !isCheckMet(c, deps)).map(describeCheck);
}

// ----------------------------------------------------------------- apply

/**
 * Applies a beat and resolves once it is shown (status 'ready'), when the
 * readiness timeout passes with the parts still missing or an action had to
 * be skipped ('not-ready'), or when the signal aborts ('cancelled'). Actions
 * that cannot run because an engine or the loaded asset never appeared are
 * skipped and listed; a real failure (a procedure that fails to load, an
 * invalid time) rejects.
 */
export async function applyBeat(
  beat: Beat,
  deps: ControllerDeps,
  opts: ApplyOptions = {},
): Promise<BeatOutcome> {
  const started = deps.now();
  const ctx: WaitContext = {
    deps,
    signal: opts.signal,
    engineTimeoutMs: opts.engineTimeoutMs ?? ENGINE_TIMEOUT_MS,
    pollMs: opts.pollMs ?? POLL_MS,
  };
  const cancelled: BeatOutcome = { status: 'cancelled', beatId: beat.id };
  if (ctx.signal?.aborted) return cancelled;

  try {
    if (beat.mode) goToMode(beat.mode, deps);
    applyBeatState(beat.state, deps);
  } catch (err) {
    throw new Error(`Beat ${beat.id}: state failed: ${errorText(err)}`, { cause: err });
  }

  const skipped: string[] = [];
  for (const action of beat.actions) {
    if (ctx.signal?.aborted) return cancelled;
    let result: StepResult;
    try {
      result = await runAction(action, ctx);
    } catch (err) {
      throw new Error(`Beat ${beat.id}: ${describeAction(action)} failed: ${errorText(err)}`, { cause: err });
    }
    if (result === CANCELLED) return cancelled;
    if (result) skipped.push(result);
  }

  const readyTimeoutMs = opts.readyTimeoutMs ?? READY_TIMEOUT_MS;
  const r = await waitFor(() => missingChecks(beat.ready, deps).length === 0, ctx, readyTimeoutMs);
  if (r === CANCELLED) return cancelled;
  const elapsedMs = deps.now() - started;
  // A skipped action never ran, so the beat is not fully shown even when the checks pass later.
  if (r === 'ok' && skipped.length === 0) return { status: 'ready', beatId: beat.id, skipped, elapsedMs };
  return {
    status: 'not-ready',
    beatId: beat.id,
    missing: r === 'ok' ? [] : missingChecks(beat.ready, deps),
    skipped,
    elapsedMs,
  };
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------- freeze

/** Everything freeze() changed, so unfreeze() can put it back exactly. */
export interface FreezeSnapshot {
  clock: { live: boolean; playing: boolean; multiplier: number; simTime: number };
  liveSource: LiveSource;
  failureScenarios: string[];
  procedure: { id: string; playing: boolean } | null;
}

export type FreezeDeps = Pick<ControllerDeps, 'clock' | 'twin' | 'now'>;

/**
 * Holds the picture still: a live clock becomes simulated time at this
 * instant and the clock pauses; the twin data source goes 'off' (which stops
 * the simulator and the live feed); a playing procedure pauses. Failure
 * scenarios are cleared too, because the twin runtime switches an 'off'
 * source back to 'sim' while any scenario is active.
 */
export function freeze(deps: FreezeDeps): FreezeSnapshot {
  const clock = deps.clock();
  const twin = deps.twin();
  const snapshot: FreezeSnapshot = {
    clock: { live: clock.live, playing: clock.playing, multiplier: clock.multiplier, simTime: clock.simTime },
    liveSource: twin.liveSource,
    failureScenarios: [...twin.failureScenarios],
    procedure: twin.procedure ? { id: twin.procedure.procedure.id, playing: twin.procedure.playing } : null,
  };
  if (clock.live) clock.setSimTime(deps.now());
  deps.clock().setPlaying(false);
  if (snapshot.failureScenarios.length > 0) twin.setFailureScenarios([]);
  twin.setLiveSource('off');
  if (snapshot.procedure?.playing) twin.setProcedurePlaying(false);
  return snapshot;
}

/** Puts back what freeze() changed. A live clock returns to wall time; a simulated one resumes from where it stopped. */
export function unfreeze(snapshot: FreezeSnapshot, deps: FreezeDeps): void {
  const clock = deps.clock();
  if (snapshot.clock.live) clock.setLive();
  else clock.setSimTime(snapshot.clock.simTime);
  clock.setMultiplier(snapshot.clock.multiplier);
  clock.setPlaying(snapshot.clock.playing);
  const twin = deps.twin();
  twin.setLiveSource(snapshot.liveSource);
  if (snapshot.failureScenarios.length > 0) twin.setFailureScenarios([...snapshot.failureScenarios]);
  const running = deps.twin().procedure;
  if (snapshot.procedure?.playing && running && running.procedure.id === snapshot.procedure.id) {
    twin.setProcedurePlaying(true);
  }
}
